import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useBrand } from '../../context/BrandContext';
import { useCopy } from '../../context/CopyContext';
import Header from '../Header';
import Heading from '../Heading';
import Button from '../Button';
import PoweredBy from '../PoweredBy';
import ItemsModalView from './ItemsModalView';

const WelcomeView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { brandConfig } = useBrand();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);

  const handleContinue = () => {
    dispatch({ type: 'SET_STEP', payload: 2 });
    navigate('/before-start');
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  return (
    <div className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden" data-node-id="44:219">
      {/* Background Video */}
      <video 
        key={brandConfig.video.background.src}
        className="absolute inset-0 w-full h-full object-cover z-0"
        autoPlay={brandConfig.video.background.autoplay}
        muted={brandConfig.video.background.muted}
        loop={brandConfig.video.background.loop}
        playsInline={brandConfig.video.background.playsInline}
      >
        <source src={brandConfig.video.background.src} type={brandConfig.video.background.type} />
      </video>

      {/* Container with flexbox structure - no padding here */}
      <div className="flex-1 flex flex-col gap-24 relative z-10">
        
        {/* Header Section - with padding */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header 
              showItems={state.itemsCount > 0} 
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
              logoColorOverride="dark"
            />
          </div>
        </div>

        {/* Main Content Area - with padding */}
        <motion.div 
          className="flex-1 grid grid-rows-3 gap-0 mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {/* Row 1 - Empty by default */}
          <div className="flex items-center justify-center">
            {/* Content can be placed here for different pages */}
          </div>

          {/* Row 2 - Default heading location */}
          <div className="flex items-center justify-center">
            <div className="text-center">
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ 
                  duration: 0.6, 
                  ease: [0.25, 0.1, 0.25, 1]
                }}
              >
                <Heading 
                  title={getCopy('welcome.heading')}
                  className="[&>div]:!text-white"
                />
              </motion.div>
            </div>
          </div>

          {/* Row 3 - Empty by default */}
          <div className="flex items-center justify-center">
            {/* Content can be placed here for different pages */}
          </div>
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Button */}
          <div className="flex justify-center">
            <Button 
              className="bg-brand-background [&_p]:!text-brand-text-tertiary"
              variant="primary" 
              onClick={handleContinue}
            >
              {getCopy('welcome.buttons.primary')}
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy 
              textColorOverride="white"
              logoColorOverride="dark"
              isWelcomeView={true}
            />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />
    </div>
  );
};

export default WelcomeView;
